import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDownAux from "@waytek/MobiusOverrides/Icons/ChevronDownAux";
import ChevronUpAux from "@waytek/MobiusOverrides/Icons/ChevronUpAux";
import Link from "@waytek/MobiusOverrides/Link/Link";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    defaultNotes?: string;
}

/* Verndale Custom Feature */
const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings as any,
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    addLinesLink?: LinkPresentationProps;
    counterGrid?: GridContainerProps;
    counterLeftItem?: GridItemProps;
    counterRightItem?: GridItemProps;
    counterTypography?: TypographyPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    editableNotesTextArea: {
        css: css`
            margin-top: 5px;
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
            resize: none;
        `,
    },
    addLinesLink: {
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
        },
    },
    counterGrid: {
        gap: 0,
        dir: "row",
        css: css`
            margin-top: 4px;

            ${GridOffset} {
                justify-content: space-between;
            }
        `,
    },
    counterLeftItem: {
        width: 12,
        css: css`
            max-width: none;
            flex-basis: auto;
        `,
    },
    counterRightItem: {
        width: 12,
        css: css`
            max-width: none;
            flex-basis: auto;
        `,
    },
    counterTypography: {
        size: 12,
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    defaultNotes,

    /* Verndale Custom Feature */
    customSettings,
}) => {
    const [showLineNotes, setShowLineNotes] = useState(false);
    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNotes);
    };

    const [notes, setNotes] = useState(cartLine.notes);
    const [count, setCount] = useState(notes.length || 0);

    useEffect(() => {
        if (defaultNotes) {
            setNotes(defaultNotes);
            setCount(defaultNotes.length);
        }
    }, [defaultNotes]);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
        setCount(event.target.value.length);
    };

    const blurHandler = () => {
        onNotesChange(notes);
    };

    const heading = showLineNotes
        ? "Hide Line Notes"
        : cartLine.notes.length === 0
        ? "Add Line Notes"
        : "Show Line Notes";

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Link
                    {...styles?.addLinesLink}
                    aria-expanded={showLineNotes}
                    htmlFor={`notes-${cartLine.id}`}
                    onClick={lineNotesClickHandler}
                    data-test-selector="cartline_notesShowHideButton"
                    icon={{
                        iconProps: { src: showLineNotes ? ChevronUpAux : ChevronDownAux, size: 10 },
                        position: "right",
                    }}
                >
                    {translate(heading)}
                </Link>
                {showLineNotes && (
                    <>
                        <TextArea
                            {...styles.editableNotesTextArea}
                            value={notes}
                            id={`notes-${cartLine.id}`}
                            disabled={!cart.canModifyOrder}
                            onChange={changeHandler}
                            onBlur={blurHandler}
                            data-test-selector="cartline_notes"
                            maxLength={customSettings.notesMaxNumberOfCharacters}
                        />

                        {customSettings.notesMaxNumberOfCharacters && (
                            <GridContainer {...styles.counterGrid}>
                                <GridItem {...styles.counterLeftItem}>
                                    <Typography {...styles.counterTypography}>
                                        {translate("Max. {0} Characters", `${customSettings.notesMaxNumberOfCharacters}`)}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.counterRightItem}>
                                    <Typography
                                        {...styles.counterTypography}
                                    >{`${count}/${customSettings.notesMaxNumberOfCharacters}`}</Typography>
                                </GridItem>
                            </GridContainer>
                        )}
                    </>
                )}
            </StyledWrapper>
        );
    }

    if (!cartLine.notes) {
        return null;
    }

    return (
        <SmallHeadingAndText
            heading={label || translate("Item Notes")}
            text={cartLine.notes}
            extendedStyles={styles.readOnlyNotesText}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineNotes));
